import { useEffect, useState, useRef, memo } from 'react';
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  IconButton,
  Popover,
  Button,
  Tooltip,
  Typography,
  OutlinedInput,
  
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import useApi from 'hooks/useApi';
import { useParams } from 'react-router';
import { useField } from 'formik';

type StatusItem = {
  _id: string;
  name: string;
  uid: string;
};

type StatusformProps = {
  name: string;
  onStatusesLoaded?: (list: StatusItem[]) => void;
  selectedNode: any;
};

const Statusform = ({ name, onStatusesLoaded, selectedNode }: StatusformProps) => {
  const { teamUid } = useParams();
  const [statuses, setStatuses] = useState<StatusItem[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [newStatus, setNewStatus] = useState('');
  const [error, setError] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const { getNodeStatus, createNodeStatus } = useApi();
 

  

  const [field, , helpers] = useField<string>(name);
  const { value: selectedUid } = field;
  const { setValue } = helpers;

  const extractNumber = (str: string) => {
  const match = str.match(/^\d+/);
  return match ? Number(match[0]) : Infinity;
};
  useEffect(() => {
    if (!teamUid) return;
 
    getNodeStatus(teamUid)
      .then((res: any) => {
        const statusList: StatusItem[] = Array.isArray(res.data)
          ? res.data.map((item: any) => ({
              _id: item._id,
              name: item.name,
              uid: item.uid
            })).sort((a:any, b:any) => {
            const numA = extractNumber(a.name);
            const numB = extractNumber(b.name);
            if (numA !== numB) return numA - numB;
            return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
          })
          : [];
        console.log("status list",statusList);
        setStatuses(statusList);
        if (onStatusesLoaded) {
          onStatusesLoaded(statusList);
        }
      })
      .catch(() => setStatuses([]));
  }, [teamUid]);  
 
  useEffect(() => {
    if (!selectedUid || statuses.length === 0) return;
    
    const found = statuses.find((s) => s.uid === selectedUid);
    if (!found) {
      const stepStatusObj = selectedNode?.data?.step?.onComplete?.status;
      if (stepStatusObj?.uid === selectedUid) {
        const updatedStatusList = [...statuses, { _id: '', name: stepStatusObj.name, uid: selectedUid }];
        console.log("updated status",updatedStatusList);
        setStatuses(updatedStatusList);
        if (onStatusesLoaded) {
          onStatusesLoaded(updatedStatusList);
        }
      }
    }
  }, [selectedUid, statuses.length]);  

  const handleSelectChange = (e: any) => {
    setValue(e.target.value);
  };

  const handleAddStatus = async () => {
    const trimmed = newStatus.trim();
    if (!trimmed) return;
    if (statuses.some((s) => s.name === trimmed)) {
      setError('Status already exists');
      return;
    }
    if (!teamUid) {
      setError('Team UID missing');
      return;
    }

    try {
      const created = await createNodeStatus({ name: trimmed }, teamUid);
      const refreshResponse = await getNodeStatus(teamUid);
      const updatedStatusList: StatusItem[] = Array.isArray(refreshResponse.data)
        ? refreshResponse.data.map((item: any) => ({
            _id: item._id,
            name: item.name,
            uid: item.uid
          })).sort((a:any, b:any) => {
            const numA = extractNumber(a.name);
            const numB = extractNumber(b.name);
            if (numA !== numB) return numA - numB;

            return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
          })
        : [];
      console.log("updatedStatusList",updatedStatusList);
      setStatuses(updatedStatusList);
      onStatusesLoaded?.(updatedStatusList);
      setValue(created?.uid);
      setAnchorEl(null);
      setNewStatus('');
      setError('');
    } catch {
      setError('Failed to add status');
    }
  };

  return (
    <Box my={1}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Select Reporting Status
      </Typography>

      <Box display="flex" alignItems="center" gap={1}>
        <FormControl fullWidth size="medium">
          <Select
            value={selectedUid || ''}
            onChange={handleSelectChange}
            displayEmpty
            renderValue={(uid) => {
              if (!uid) return <span style={{ color: '#aaa' }}>Choose status...</span>;
              const status = statuses.find((s) => s.uid === uid);
              return status ? status.name : uid;
            }}
          >
            {statuses.map((s) => (
              <MenuItem key={s.uid} value={s.uid}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        <Tooltip title="Add new status">
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ flexShrink: 0 }}>
            <AddCircleOutlineIcon sx={{ color: 'gray' }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box p={2} width={250}>
          <Typography>Add new status</Typography>
          <OutlinedInput
            fullWidth
            value={newStatus}
            onChange={(e) => {
              setNewStatus(e.target.value);
              setError('');
            }}
            inputRef={inputRef}
            onKeyDown={(e) => e.key === 'Enter' && handleAddStatus()}
            error={Boolean(error)}
          />
          {error && (
            <Typography color="error" variant="caption">
              {error}
            </Typography>
          )}
          <Box mt={1} textAlign="right">
            <Button variant="contained" onClick={handleAddStatus} disabled={!newStatus.trim()}>
              Add
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default memo(Statusform);

